import styled, { css, keyframes } from 'styled-components';
import { Form as unformForm } from '@unform/web';

// Reuse AccountsSummaryV2 color scheme
const bgDark = '#121212';
const cardDark = '#1E1E1E';
const textLight = '#EAEAEA';
const textSecondary = '#A0A0A0';
const border = 'rgba(255,255,255,0.1)';
const green = '#1DB954';
const amber = '#FFC107';
const red = '#dc3545';
const primary = '#256af4';

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: ${bgDark};
  color: ${textLight};
  align-items: center;
  width: 100%;
  overflow-x: hidden;
  * {
    box-sizing: border-box;
  }

  /* react-responsive-modal overrides */
  .v2Modal {
    background: ${cardDark};
    border: 1px solid ${border};
    border-radius: 12px;
    padding: 32px 24px;
    color: ${textLight};
    max-width: 560px;
    width: 90vw;
  }
`;

export const Form = styled(unformForm)`
  width: 100%;
  max-width: 1080px;
  margin: 120px auto 0 auto;
  display: flex;
  position: relative;

  div {
    input:not([type='checkbox']) {
      background: ${cardDark};
      color: ${textLight};
      border: 1px solid ${border};
      border-radius: 8px;
    }
  }
`;

const appear = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const Content = styled.div`
  width: 100%;
  max-width: 1080px;
  margin: 24px auto 40px auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`;

export const Title = styled.h1`
  font-size: 28px;
  font-weight: 800;
`;

export const ControlsRow = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 8px;
  align-items: center;
  overflow-x: auto;
  padding-bottom: 2px;

  @media (max-width: 640px) {
    flex-wrap: wrap;
    overflow-x: visible;
    gap: 6px;

    .filtersGroup,
    .importBtn {
      display: none;
    }
  }
`;

export const Search = styled.input`
  background: ${cardDark};
  border: 1px solid ${border};
  color: ${textLight};
  border-radius: 8px;
  padding: 10px 12px;
  min-width: 160px;
  flex: 1 1 220px;
  max-width: 420px;
`;

export const ToggleGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

export const ToggleButton = styled.button<{
  active?: boolean;
  tone?: 'in' | 'out';
}>`
  background: ${cardDark};
  border: 1px solid ${border};
  color: ${textSecondary};
  padding: 8px 12px;
  border-radius: 10px;
  font-weight: 600;
  ${(p) =>
    p.active &&
    css`
      color: ${textLight};
      border-color: ${p.tone === 'out' ? red : green};
    `}
`;

export const PrimaryButton = styled.button<{ variant?: 'primary' | 'green' }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: ${(p) => (p.variant === 'primary' ? primary : green)};
  color: ${(p) => (p.variant === 'primary' ? '#fff' : '#000')};
  border: 0;
  border-radius: 10px;
  padding: 10px 14px;
  font-weight: 700;
  white-space: nowrap;
  flex-shrink: 0;
`;

export const MonthStepperWrap = styled.div`
  flex-shrink: 0;
`;

export const Table = styled.table`
  width: 100%;
  border-spacing: 0 12px;
  animation: ${appear} 0.3s ease both;

  thead th {
    background: ${bgDark};
    color: ${textSecondary};
    font-weight: 600;
    text-align: left;
    padding: 6px 10px;
  }

  tbody tr {
    background: ${cardDark};
    border: 1px solid ${border};
    border-radius: 12px;
    overflow: hidden;
  }
  tbody td {
    padding: 10px;
    vertical-align: middle;
  }

  tbody td:first-child {
    border-top-left-radius: 12px;
    border-bottom-left-radius: 12px;
  }
  tbody td:last-child {
    border-top-right-radius: 12px;
    border-bottom-right-radius: 12px;
  }

  .amount {
    font-weight: 700;
  }
  .in {
    color: ${green};
  }
  .out {
    color: ${red};
  }
  .xfer {
    color: ${textSecondary};
  }

  /* Mobile card layout */
  @media (max-width: 640px) {
    thead {
      display: none;
    }
    tbody tr {
      display: block;
      margin: 0 0 12px 0;
      border-radius: 12px;
      overflow: hidden;
    }
    tbody td,
    tbody td:first-child,
    tbody td:last-child {
      display: flex;
      align-items: center;
      justify-content: space-between;
      border-radius: 0;
      padding: 10px 12px;
      border-bottom: 1px solid ${border};
      width: 100%;
      box-sizing: border-box;
    }
    tbody td:last-child {
      border-bottom: 0;
    }
    tbody td::before {
      content: attr(data-label);
      color: ${textSecondary};
      margin-right: 12px;
      flex: 0 0 auto;
    }
    .amount {
      justify-content: space-between;
    }
  }
`;

export const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

export const ActionButton = styled.button<{ tone?: 'normal' | 'danger' }>`
  background: transparent;
  color: ${textLight};
  border: 1px solid ${border};
  padding: 6px 10px;
  border-radius: 8px;
  ${(p) =>
    p.tone === 'danger' &&
    css`
      color: ${red};
      border-color: ${red};
    `}
`;

export const IconButton = styled.button<{ tone?: 'normal' | 'danger' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  background: transparent;
  color: ${textLight};
  border: 1px solid ${border};
  border-radius: 10px;
  ${(p) =>
    p.tone === 'danger' &&
    css`
      color: ${red};
      border-color: ${red};
    `}
  &:hover {
    background: rgba(255, 255, 255, 0.06);
  }
`;

export const SectionHeader = styled.div`
  margin-top: 24px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const SectionTitle = styled.h2`
  font-size: 28px;
  font-weight: 800;
`;

export const ModalTitle = styled.h2`
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 12px;
`;

export const SubInfo = styled.div`
  margin: -6px 0 8px 0;
  color: ${textSecondary};
  font-size: 14px;
`;

export const ModalRow = styled.div`
  display: flex;
  gap: 8px;
  margin: 8px 0;
`;

export const ModalFooter = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 12px;
`;

export const InlineFields = styled.div`
  display: flex;
  gap: 8px;

  & > div {
    flex: 1 1 0;
    min-width: 0;
    box-sizing: border-box;
  }

  /* Adjust proportions: 70% amount, 30% installments */
  & > div:first-child {
    flex: 0 1 70%;
  }
  & > div:last-child {
    flex: 0 1 30%;
    min-width: 120px;
    max-width: 30%;
  }
`;

export const UniformDate = styled.div`
  input {
    background: ${cardDark} !important;
    color: ${textLight} !important;
    border: 0 !important;
  }
`;

export const InstallmentsTipWrap = styled.div`
  /* Anchor the tip icon inside the input container */
  & > div {
    position: relative;
  }
  /* Tip container is the last child inside Input Container */
  & > div > div:last-child {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
  }
`;

export const ImportRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 8px 0 16px 0;
`;

export const FileButton = styled.label`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  background: ${amber};
  color: #000;
  border-radius: 8px;
  cursor: pointer;
`;

export const FileName = styled.span`
  color: ${textSecondary};
  font-size: 14px;
`;

export const DropZone = styled.div<{ active?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: 140px;
  padding: 16px;
  border: 2px dashed ${(p) => (p.active ? amber : border)};
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.03);
  color: ${textSecondary};
  cursor: pointer;
  user-select: none;

  &:hover {
    border-color: ${amber};
  }
`;

export const DropText = styled.div`
  text-align: center;
  font-size: 14px;
  color: ${textSecondary};
`;

export const CheckboxRow = styled.div`
  display: flex;
  align-items: center;
  margin: 4px 0;

  /* Ensure the custom checkbox box is visible and not shrunk */
  label input[type='checkbox'] {
    width: 13px;
    height: 13px;
    flex: 0 0 13px;
  }

  /* Override large default margins from inner checkbox container */
  & > * {
    margin: 6px 0 !important;
  }
`;
